// moveoutFundsClient — client for the WEL-48 Move-Out Funds Direction Agreement.
//
// Two surfaces in one module (mirrors the intake split elsewhere in the app):
//
//   • PUBLIC, token-gated (no Bearer): the resident-facing intake form at
//     /moveout-funds/:token. The token in the path is the only credential, so
//     these calls go straight to fetch (NOT the authed `api` helper) exactly
//     like lib/intakeClient.ts / lib/verificationClient.ts.
//       - GET  /api/moveout-funds/intake/:token  → MoveoutFundsIntakeView
//       - POST /api/moveout-funds/intake/:token  → MoveoutFundsIntakeSubmitResult
//
//   • AUTHENTICATED staff (Bearer via `api`): initiate the intake collection on
//     a current tenancy, and void+restart it.
//       - POST /api/moveout-funds/:groupId/initiate       → InitiateResult
//       - POST /api/moveout-funds/:groupId/resend-intake   → ResendIntakeResult
//
// Wire shapes are written against the backend contract in
// apps/api/src/lib/moveoutFunds.ts (MoveoutFundsIntake) + the routes the backend
// agent is building. Reconciled at integration — see the report for the exact
// shapes assumed.

import { api } from './api';
import type { MoveoutFundsIntakeSubmission } from './moveoutFundsForm';

// Re-export the dependency-free form helpers/types so app call sites can import
// everything from this one client module (the helpers live in moveoutFundsForm
// so they stay unit-testable without pulling in the network `api` chain).
export {
  MOVEOUT_COSIGNER_PREFIX,
  MOVEOUT_OTHER_CHOICE,
  buildMoveoutFundsSubmission,
  canSubmitMoveoutFunds,
  cosignerChoice,
  isCosignerChoice,
} from './moveoutFundsForm';
export type {
  MoveoutFundsFormDraft,
  MoveoutFundsIntakeSubmission,
} from './moveoutFundsForm';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// PUBLIC intake form (token-gated, no auth)
// ---------------------------------------------------------------------------

/**
 * Public form lifecycle state (server-driven; never recomputed client-side):
 *   • 'fillable'  — the form is open; this party can designate the recipient.
 *   • 'locked'    — another resident/co-signer already completed it first
 *                   (first-valid-completion wins); nothing to do.
 *   • 'submitted' — THIS request was just submitted by this party.
 *   • 'cancelled' — staff voided the open intake (resend-intake / cancel).
 *   • 'unavailable' — the link is valid but there is nothing to fill out.
 */
export type MoveoutFundsIntakeState =
  | 'fillable'
  | 'locked'
  | 'submitted'
  | 'cancelled'
  | 'unavailable';

/** One current resident the requested party can designate as the recipient. */
export interface MoveoutFundsResidentOption {
  /** AppFolio tenant id — POSTed back as recipientResidentTenantId. */
  tenantId: string;
  /** Display name for the radio option. */
  name: string;
}

/** One co-signer the requested party can designate as the recipient (WEL-143). */
export interface MoveoutFundsCosignerOption {
  /** AppFolio tenant id — POSTed back as recipientCosignerTenantId. */
  tenantId: string;
  name: string;
}

/** The public form view returned by GET /api/moveout-funds/intake/:token. */
export interface MoveoutFundsIntakeView {
  state: MoveoutFundsIntakeState;
  /** The premises this agreement concerns (shown for context). */
  premises: string | null;
  /** Property address for the brand lockup header (often === premises). */
  propertyAddress: string | null;
  /** The requested party's own display name (for a friendly greeting). */
  recipientName: string | null;
  /** The current residents this party can designate as the recipient. */
  residents: MoveoutFundsResidentOption[];
  /** The co-signers this party can designate as the recipient (WEL-143). */
  cosigners: MoveoutFundsCosignerOption[];
  /**
   * When state === 'locked': who already completed it + their pick, so the
   * "someone already did this" state can name the chosen recipient. Optional —
   * absent on a bare lock.
   */
  lockedSelection?: {
    designatedRecipientName: string;
    completedByName?: string;
  };
}

export interface MoveoutFundsIntakeSubmitResult {
  ok: true;
  /** The refreshed view — 'submitted' on success, or 'locked' if beaten to it. */
  view: MoveoutFundsIntakeView;
}

export class MoveoutFundsApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`Move-out funds API error ${status}`);
  }
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new MoveoutFundsApiError(res.status, body);
  }
  return (await res.json()) as T;
}

// The ACTUAL wire shapes the backend returns (apps/api/src/routes/moveoutFunds.ts
// MoveoutFundsFormView + the submit response). The page consumes the richer
// MoveoutFundsIntakeView above; we map raw → view here so the page stays intact.
interface RawFormView {
  state: 'fillable' | 'locked' | 'sent' | 'unavailable';
  premises: string;
  residents: Array<{ tenantId: string; name: string }>;
  cosigners?: Array<{ tenantId: string; name: string }>;
  recipient: { key: string; name: string } | null;
  alreadyLocked: boolean;
}
interface RawSubmitResult {
  ok: boolean;
  state: 'fillable' | 'locked' | 'sent' | 'unavailable';
  alreadyLocked: boolean;
  /** Present only when THIS submission won the lock + the agreement was sent. */
  docusealSubmissionId?: number;
}

function mapFormView(raw: RawFormView): MoveoutFundsIntakeView {
  // To a fresh viewer, 'sent' (agreement already out) reads the same as 'locked'.
  const state: MoveoutFundsIntakeState = raw.state === 'sent' ? 'locked' : raw.state;
  return {
    state,
    premises: raw.premises || null,
    propertyAddress: raw.premises || null,
    recipientName: raw.recipient?.name ?? null,
    residents: raw.residents ?? [],
    cosigners: raw.cosigners ?? [],
  };
}

/** GET the public, token-gated intake view. */
export async function getMoveoutFundsIntake(token: string): Promise<MoveoutFundsIntakeView> {
  const raw = await publicRequest<RawFormView>(
    `/api/moveout-funds/intake/${encodeURIComponent(token)}`,
  );
  return mapFormView(raw);
}

/** POST the recipient designation. Returns the refreshed view (submitted / locked). */
export async function submitMoveoutFundsIntake(
  token: string,
  body: MoveoutFundsIntakeSubmission,
): Promise<MoveoutFundsIntakeSubmitResult> {
  const raw = await publicRequest<RawSubmitResult>(
    `/api/moveout-funds/intake/${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  // We won the lock + the agreement sent → 'submitted' (this party did it).
  // Beaten to it / dormant → reflect the live state. The page merges this over
  // the loaded view, so premises/propertyAddress/residents carry forward.
  let state: MoveoutFundsIntakeState;
  if (raw.docusealSubmissionId != null) state = 'submitted';
  else if (raw.state === 'unavailable') state = 'unavailable';
  else if (raw.alreadyLocked) state = 'locked';
  else state = 'submitted';
  return {
    ok: true,
    view: {
      state,
      premises: null,
      propertyAddress: null,
      recipientName: null,
      residents: [],
      cosigners: [],
    },
  };
}

// ---------------------------------------------------------------------------
// AUTHENTICATED staff actions (Bearer via `api`)
// ---------------------------------------------------------------------------

/** One signer the intake will be sent to (residents + co-signers), for the preview. */
export interface MoveoutFundsInitiatePerson {
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  /** Verbatim AppFolio TenantType ('Financially Responsible' | 'Co-signer'). */
  tenantType: string | null;
}

export interface InitiateMoveoutFundsBody {
  propertyId: string;
  unitId: string;
  /**
   * The (possibly edited) roster of residents + co-signers who receive the
   * intake link. Omit to let the backend re-derive from AppFolio.
   */
  recipients?: MoveoutFundsInitiatePerson[];
  /**
   * Staff override for the occupancy's lease start date (yyyy-mm-dd). The backend
   * freezes it on the intake record; REQUIRED when AppFolio carries none on file
   * (else initiate answers 400 `lease_start_date_required`). The move-out staff
   * send UI must collect + require this when the resolved value is empty.
   */
  leaseStartDate?: string;
}

export interface InitiateMoveoutFundsResult {
  groupId: string;
  /** How many intake links went out. */
  sent?: number;
  /** Names whose email was missing/blank (couldn't be emailed). */
  missingEmailNames?: string[];
  [key: string]: unknown;
}

/**
 * Initiate the Move-Out Funds Direction Agreement on a current tenancy: send
 * the recipient-designation intake to all current residents + co-signers.
 * `:groupId` identifies the tenancy on the server; for a fresh send we derive
 * the group server-side from property+unit, so the path uses the unit's group.
 */
export async function initiateMoveoutFunds(
  groupId: string,
  body: InitiateMoveoutFundsBody,
): Promise<InitiateMoveoutFundsResult> {
  // Backend returns { groupId, round, status, recipientsEmailed,
  // recipients: [{ key, name, kind, hasEmail }] }.
  const raw = await api.post<{
    groupId: string;
    recipientsEmailed?: number;
    recipients?: Array<{ name: string; hasEmail: boolean }>;
  }>(`/api/moveout-funds/${encodeURIComponent(groupId)}/initiate`, body);
  return {
    groupId: raw.groupId ?? groupId,
    sent: raw.recipientsEmailed ?? 0,
    missingEmailNames: (raw.recipients ?? []).filter((r) => !r.hasEmail).map((r) => r.name),
  };
}

export interface ResendMoveoutFundsIntakeResult {
  ok: boolean;
  /** How many intake links were re-sent after the void+restart. */
  sent?: number;
  [key: string]: unknown;
}

/**
 * Void the open agreement (if any) and restart the intake collection from
 * scratch — the Requests-dashboard "Resend intake" quick action (WEL-48). The
 * first valid completion of the restarted intake re-locks the selection.
 */
export async function resendMoveoutFundsIntake(
  groupId: string,
): Promise<ResendMoveoutFundsIntakeResult> {
  // Backend returns { groupId, round, voidedSubmissionId, recipientsEmailed }.
  const raw = await api.post<{ recipientsEmailed?: number }>(
    `/api/moveout-funds/${encodeURIComponent(groupId)}/resend-intake`,
  );
  return { ok: true, sent: raw.recipientsEmailed ?? 0 };
}
